import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  ArrowRight, Heart, Target, Users, Lightbulb, Trophy, Shield,
  BookOpen, Globe, Award, Star, CheckCircle2, Cpu, Bot, Brain,
  GraduationCap, Sparkles, Code, Gamepad2, BarChart3, Zap
} from 'lucide-react';

export default function AboutPage() {
  const subjects = [
    { icon: Code, name: 'Coding & Programming', desc: 'Python, Java, JavaScript, Scratch, C++' },
    { icon: Cpu, name: 'CAD & 3D Design', desc: 'Computer-aided design and modeling' },
    { icon: Brain, name: 'AI & Machine Learning', desc: 'Neural networks, data science, ML basics' },
    { icon: Bot, name: 'Robotics & IoT', desc: 'FIRST, VEX, LEGO SPIKE, Arduino' },
    { icon: Shield, name: 'Cybersecurity', desc: 'Digital safety, encryption, ethical hacking' },
    { icon: BarChart3, name: 'Data Science', desc: 'Analytics, visualization, pandas, matplotlib' },
    { icon: Gamepad2, name: 'Game Development', desc: 'Unity, Scratch, Pygame, game design' },
    { icon: Globe, name: 'Web & Mobile Dev', desc: 'HTML, CSS, React, app development' },
    { icon: Sparkles, name: 'AR/VR Experiences', desc: 'Augmented and virtual reality creation' },
    { icon: Zap, name: 'Digital Confidence', desc: 'Tech literacy, digital citizenship' },
  ];

  const impactNumbers = [
    { value: '12,000+', label: 'Learners Served', detail: 'across all programs since founding' },
    { value: '28', label: 'Area Schools', detail: 'partnered for after-school programs' },
    { value: '93%', label: 'Below AMI', detail: 'students from families below Area Median Income' },
    { value: '49%', label: 'Female & Non-Binary', detail: 'closing the gender gap in STEM' },
    { value: 'Top 5%', label: 'STEM.org Rated', detail: 'Best In STEM™ + Accredited™ Trustmark' },
    { value: '10+', label: 'STEM Subjects', detail: 'coding, robotics, AI, cybersecurity & more' },
  ];

  const demographics = [
    { label: 'Latino/Hispanic', value: 54 },
    { label: 'Black/African American', value: 14 },
    { label: 'Multi-racial', value: 12 },
    { label: 'White', value: 11 },
    { label: 'Asian', value: 8 },
    { label: 'Native American', value: 1 },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 gradient-hero overflow-hidden">
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">
              About <span className="text-rsc-yellow">Ready Set STEM</span>
            </h1>
            <p className="text-xl text-white/90 mb-4">
              Ready Set STEM is a STEM.org-accredited educational platform born from APS Training Academy — 
              a 501(c)(3) non-profit with a singular mission: ensuring every child can access world-class 
              STEM education, regardless of their family's income or background.
            </p>
            <p className="text-lg text-white/70">
              We've served <strong className="text-rsc-yellow">12,000+ learners</strong> across <strong className="text-rsc-yellow">28 area schools</strong>, 
              with <strong className="text-rsc-yellow">93%</strong> of our students coming from families below the Area Median Income.
            </p>
          </div>
        </div>
      </section>

      {/* STEM.org Best In STEM™ Recognition */}
      <section className="py-10 bg-gradient-to-br from-amber-50 to-yellow-50 border-b border-amber-200">
        <div className="container-app">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start gap-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h2 className="font-heading text-2xl font-black text-rsc-navy">Best In STEM™ + Accredited™ Trustmark</h2>
                <span className="px-3 py-1 bg-amber-200 text-amber-800 text-xs font-bold rounded-full">TOP 5%</span>
              </div>
              <blockquote className="text-gray-700 border-l-4 border-amber-400 pl-4 mb-4 italic">
                "Ready Set STEM has been selected as a Best In STEM™ organization by STEM.org and has also 
                earned the STEM.org Accredited™ Trustmark — a dual recognition reserved for programs that demonstrate 
                exceptional quality, rigor, and integrity in STEM education."
              </blockquote>
              <p className="text-gray-600 text-sm">
                STEM.org's evaluation assesses program design, instructional quality, learner outcomes, equity of access, 
                and continuous improvement — ensuring that opportunity in STEM education is not determined by circumstance, 
                but by commitment to quality.
              </p>
              <p className="text-xs text-gray-500 mt-2">— <a href="https://stem.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">STEM.org</a> | Educational Research</p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact by the Numbers */}
      <section className="py-12 bg-white">
        <div className="container-app">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {impactNumbers.map((stat, i) => (
              <div key={i} className="text-center p-4 rounded-xl hover:bg-gray-50 transition-colors">
                <p className="text-3xl font-black text-rsc-navy">{stat.value}</p>
                <p className="font-semibold text-sm text-rsc-blue">{stat.label}</p>
                <p className="text-xs text-gray-500 mt-1">{stat.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Origin Story */}
      <section className="section-padding bg-gray-50">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-rsc-blue font-semibold text-sm mb-4">
                <Heart className="w-4 h-4" /> OUR ORIGIN
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-6">
                Born from a Non-Profit Mission
              </h2>
              <p className="text-gray-700 text-lg mb-4">
                Ready Set STEM was created by <strong>APS Training Academy</strong>, a 501(c)(3) non-profit founded 
                by <strong>Harish Ananthapadmanabhan</strong> in Aurora, Illinois, to honor his mother 
                <strong> Leela Padmanabhan</strong> and continue her lifelong legacy of community service.
              </p>
              <p className="text-gray-700 text-lg mb-4">
                What began as free after-school coding classes for underserved children — originally known as 
                <em className="text-rsc-navy font-semibold">Ready Set Code</em> — has grown into a comprehensive 
                STEM education platform serving <strong>12,000+ learners</strong> across <strong>28 area schools</strong>. 
                Our curriculum spans 10+ STEM subjects — from Scratch and Python to AI, robotics, cybersecurity, 
                and augmented reality.
              </p>
              <p className="text-gray-700 text-lg mb-6">
                Today, Ready Set STEM balances <strong>affordability with excellence, and access with rigor</strong> — 
                offering free programs for qualifying families through APS Training Academy, while also providing 
                premium courses that any family can access at accessible price points.
              </p>
              <div className="flex gap-4">
                <Link to="/community-impact">
                  <Button className="btn-primary">Our Community Impact <ArrowRight className="ml-2 h-4 w-4" /></Button>
                </Link>
                <a href="https://www.apstrainingacademy.org" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline">Visit APS Training Academy</Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="p-6 bg-white rounded-2xl shadow-lg border-l-4 border-rsc-blue">
                    <p className="text-3xl font-black text-rsc-navy">54%</p>
                    <p className="text-sm text-gray-600">Latino/Hispanic learners</p>
                  </div>
                  <div className="p-6 bg-white rounded-2xl shadow-lg border-l-4 border-purple-500">
                    <p className="text-3xl font-black text-rsc-navy">14%</p>
                    <p className="text-sm text-gray-600">Black/African American</p>
                  </div>
                  <div className="p-6 bg-white rounded-2xl shadow-lg border-l-4 border-green-500">
                    <p className="text-3xl font-black text-rsc-navy">12%</p>
                    <p className="text-sm text-gray-600">Multi-racial learners</p>
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="p-6 bg-white rounded-2xl shadow-lg border-l-4 border-orange-500">
                    <p className="text-3xl font-black text-rsc-navy">49%</p>
                    <p className="text-sm text-gray-600">Female & non-binary</p>
                  </div>
                  <div className="p-6 bg-white rounded-2xl shadow-lg border-l-4 border-rsc-yellow">
                    <p className="text-3xl font-black text-rsc-navy">93%</p>
                    <p className="text-sm text-gray-600">Below Area Median Income</p>
                  </div>
                  <div className="p-6 bg-rsc-navy rounded-2xl shadow-lg">
                    <p className="text-3xl font-black text-rsc-yellow">FREE</p>
                    <p className="text-sm text-white/70">For qualifying families</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding">
        <div className="container-app">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <div className="text-center p-8 rounded-2xl bg-rsc-blue/5 border border-rsc-blue/10">
              <div className="w-16 h-16 rounded-2xl bg-rsc-blue flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-rsc-navy mb-4">Our Mission</h2>
              <p className="text-gray-700 text-lg">
                To empower communities through accessible, high-quality STEM education that builds technical fluency, 
                ethical judgment, collaborative problem-solving, and digital confidence in every young learner — 
                regardless of their background or family income.
              </p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-rsc-yellow/5 border border-rsc-yellow/10">
              <div className="w-16 h-16 rounded-2xl bg-rsc-yellow flex items-center justify-center mx-auto mb-6">
                <Lightbulb className="w-8 h-8 text-rsc-navy" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-rsc-navy mb-4">Our Vision</h2>
              <p className="text-gray-700 text-lg">
                A world where every child has the opportunity to explore technology, develop future-ready skills, 
                and discover their potential — where opportunity in STEM is not determined by circumstance, 
                but by commitment to quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Teach */}
      <section className="section-padding bg-rsc-navy text-white">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">10+ STEM Subjects</h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Our STEM.org-accredited curriculum spans the full spectrum of technology education — 
              from first blocks for 5-year-olds to AI and cybersecurity for teens.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {subjects.map((subject, i) => {
              const Icon = subject.icon;
              return (
                <Card key={i} className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors">
                  <CardContent className="p-5 text-center">
                    <Icon className="w-8 h-8 text-rsc-yellow mx-auto mb-3" />
                    <h3 className="font-bold text-white text-sm mb-1">{subject.name}</h3>
                    <p className="text-[11px] text-white/60">{subject.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { icon: Heart, title: 'Equity & Access', desc: 'Every child deserves quality STEM education. We offer free programs through APS Training Academy for families below AMI, and keep all courses affordable.' },
              { icon: Star, title: 'Excellence & Rigor', desc: 'STEM.org\'s Top 5% recognition reflects our commitment to curriculum quality, instructor training, and measurable learner outcomes.' },
              { icon: Users, title: 'Community & Belonging', desc: '49% female/non-binary enrollment. 6+ ethnic communities represented. We build diverse, inclusive classrooms where every learner belongs.' },
              { icon: Sparkles, title: 'Innovation & Joy', desc: 'Gaming-themed rewards, competitive robotics, coding challenges — we make STEM education genuinely exciting for kids aged 5-18.' },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rsc-blue/10 to-rsc-yellow/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-rsc-blue" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-rsc-navy mb-2">{v.title}</h3>
                  <p className="text-gray-600 text-sm">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="section-padding bg-gradient-to-br from-rsc-blue/5 to-rsc-yellow/5">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">What Sets Us Apart</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">No other coding school combines all of these in one platform.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Trophy, title: 'Best In STEM™ (Top 5%)', desc: 'Dual STEM.org recognition for quality, rigor, and integrity. Only the top 5% of programs earn this distinction.' },
              { icon: Heart, title: 'Non-Profit Foundation', desc: 'Powered by APS Training Academy, a 501(c)(3). Free STEAM education for underserved families. 93% of learners below AMI.' },
              { icon: Bot, title: 'Competitive Robotics', desc: 'FIRST (FLL, FTC) and VEX (IQ, VRC) competition teams. 6 active teams, 5+ tournament awards this season.' },
              { icon: Gamepad2, title: 'Gamified Learning', desc: 'Points, badges, trading card rewards, gaming gift cards, coding challenges — all age-gated for ages 5-18.' },
              { icon: Globe, title: '10+ STEM Subjects', desc: 'Coding, CAD, AI, robotics, IoT, cybersecurity, data science, game dev, AR, web/mobile — the broadest curriculum.' },
              { icon: GraduationCap, title: '4-Level Curriculum Path', desc: 'Explorer → Builder → Coder → Innovator. Structured progression from K-2 through high school with STEM.org accreditation.' },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-rsc-navy flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-rsc-yellow" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-rsc-navy mb-1">{f.title}</h3>
                    <p className="text-gray-600 text-sm">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding gradient-hero">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">Join Our Community</h2>
          <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
            Whether you're a parent, student, educator, or community partner — there's a place for you at Ready Set STEM.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/catalog"><Button className="btn-secondary text-lg px-8 py-6">Browse Classes <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/robotics"><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">Robotics Division</Button></Link>
            <Link to="/community-impact"><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">Community Impact</Button></Link>
            <Link to="/contact"><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">Contact Us</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
